"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";
import { useOverlayPosition, type OverlayPlacement } from "./use-overlay-position";

export type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  placement?: OverlayPlacement;
  className?: string;
  wrapperClassName?: string;
  delay?: number;
};

export function Tooltip({ content, children, placement = "top-start", className, wrapperClassName, delay = 300 }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<number | null>(null);
  const position = useOverlayPosition(open, anchorRef, placement, "content", 6, tooltipRef);

  function show() {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setOpen(true), delay);
  }
  function hide() {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setOpen(false);
  }
  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  return (
    <span ref={anchorRef} className={cn("inline-flex", wrapperClassName)} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {open && position && typeof document !== "undefined"
        ? createPortal(
            <span
              ref={tooltipRef}
              role="tooltip"
              style={position}
              className={cn("pointer-events-none max-w-xs rounded-control border border-card-border bg-surface-elevated px-ds-2 py-ds-1 text-micro text-foreground shadow-floating", className)}
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
